/**
 * Shared liquid cooling CDU list helpers — grouping and table cell display (#996 Phase 3).
 * Mirrors coolingSystemListUtils.
 *
 * The section-agnostic core (grouping, reorder, session patch, duplicate naming,
 * validation, draft, legacy synthesis) comes from the shared repeatable-section
 * factory. CDU-specific table column display stays here.
 */

import { DEFAULT_LIQUID_COOLING_CDU_CONFIGURATION } from '../../types/liquidCoolingCdu';
import {
  generateAutoLiquidCoolingCduName,
  isAutoGeneratedLiquidCoolingCduName,
} from '../../utils/liquidCoolingCduAutoName';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  createRepeatableSectionListUtils,
  parseOptionalNumber as parseOptionalNumberCore,
} from '../repeatable/repeatableSectionListUtils';

import type {
  LiquidCoolingCduConfiguration,
  GroupedLiquidCoolingCduConfigurations,
  UpdateLiquidCoolingCduConfigurationInput,
} from '../../types/liquidCoolingCdu';

/** Desktop web columns (#996). TYPE carries the descriptive copy (leak-detection subline on compact). */
export const LIQUID_COOLING_CDU_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'TYPE',
  'LEAK',
  'REDUNDANCY',
  'ROOM',
] as const;

/**
 * Compact mirrors desktop column order; REDUNDANCY folds away on narrow screens.
 * ROOM (#999 Story 3.4) stays on compact so the location tag stays visible.
 * Desktop: NAME | TYPE | LEAK | REDUNDANCY | ROOM
 * Compact: NAME (+redundancy) | TYPE | ROOM
 */
export const LIQUID_COOLING_CDU_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'TYPE',
  'ROOM',
] as const;

/**
 * Compact skeleton (#999 Option B) — uniform 4-column layout shared by the compact
 * layout on native mobile AND narrow/mobile web. Adds a REDUNDANCY column
 * (relocated out of the NAME subline) so the CDU table matches the other device
 * types' four columns. Wide desktop web keeps
 * {@link LIQUID_COOLING_CDU_TABLE_DESKTOP_COLUMNS}; the 3-column
 * {@link LIQUID_COOLING_CDU_TABLE_COMPACT_COLUMNS} is retained only for the legacy
 * type union.
 * Compact: NAME (+pill) | TYPE (+Leak:) | REDUNDANCY | ROOM
 */
export const LIQUID_COOLING_CDU_TABLE_NATIVE_COMPACT_COLUMNS = [
  'NAME',
  'TYPE',
  'REDUNDANCY',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const LIQUID_COOLING_CDU_TABLE_COLUMNS =
  LIQUID_COOLING_CDU_TABLE_DESKTOP_COLUMNS;

export type LiquidCoolingCduTableLayout = 'desktop' | 'compact';

/** Injected only when `showStatusColumn` is set (Devices view). */
export const LIQUID_COOLING_CDU_TABLE_STATUS_COLUMN = 'STATUS' as const;

export type LiquidCoolingCduTableColumnKey =
  | (typeof LIQUID_COOLING_CDU_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof LIQUID_COOLING_CDU_TABLE_COMPACT_COLUMNS)[number]
  | (typeof LIQUID_COOLING_CDU_TABLE_NATIVE_COMPACT_COLUMNS)[number]
  | typeof LIQUID_COOLING_CDU_TABLE_STATUS_COLUMN;

export const LIQUID_COOLING_CDU_TABLE_COLUMN_ALIGN: Record<
  LiquidCoolingCduTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  TYPE: 'center',
  LEAK: 'left',
  REDUNDANCY: 'center',
  ROOM: 'left',
};

export type LiquidCoolingCduListSection = 'existing' | 'new';

// ---------------------------------------------------------------------------
// Section-agnostic core (delegated to the shared repeatable-section factory)
// ---------------------------------------------------------------------------

const core = createRepeatableSectionListUtils<
  LiquidCoolingCduConfiguration,
  UpdateLiquidCoolingCduConfigurationInput
>({
  defaultConfiguration: DEFAULT_LIQUID_COOLING_CDU_CONFIGURATION,
  generateAutoName: generateAutoLiquidCoolingCduName,
  isAutoGeneratedName: isAutoGeneratedLiquidCoolingCduName,
  namePrefix: 'Liquid Cooling ',
  newLabel: 'New Liquid Cooling',
  nameNoun: 'Liquid Cooling',
  getDisplayNameFallback: unit => unit.lcDescription?.trim() || undefined,
  legacySourceKeys: [
    'lcCduType',
    'lcVertivSuppliesRowManifolds',
    'lcVertivSuppliesRackManifolds',
    'lcCduRedundancy',
    'lcLeakDetectionApproach',
    'lcDescription',
  ],
  mapLegacyFields: source => ({
    lcCduType: source.lcCduType as string | undefined,
    lcVertivSuppliesRowManifolds: source.lcVertivSuppliesRowManifolds as
      | boolean
      | undefined,
    lcVertivSuppliesRackManifolds: source.lcVertivSuppliesRackManifolds as
      | boolean
      | undefined,
    lcCduRedundancy: source.lcCduRedundancy as string | undefined,
    lcLeakDetectionApproach: source.lcLeakDetectionApproach as
      | string
      | undefined,
    lcDescription: source.lcDescription as string | undefined,
  }),
});

export function normalizeLiquidCoolingCduStatus(
  status: LiquidCoolingCduConfiguration['status'] | string | undefined,
): LiquidCoolingCduConfiguration['status'] {
  return core.normalizeStatus(status);
}

export function groupLiquidCoolingCdusByStatus(
  units: LiquidCoolingCduConfiguration[],
): GroupedLiquidCoolingCduConfigurations {
  return core.groupByStatus(units);
}

export function sortLiquidCoolingCdusByDisplayOrder(
  units: LiquidCoolingCduConfiguration[],
): LiquidCoolingCduConfiguration[] {
  return core.sortByDisplayOrder(units);
}

/** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
export function applyLiquidCoolingCduSectionReorder(
  sortedUnits: LiquidCoolingCduConfiguration[],
  section: LiquidCoolingCduListSection,
  fromIndex: number,
  toIndex: number,
): LiquidCoolingCduConfiguration[] {
  return core.applySectionReorder(sortedUnits, section, fromIndex, toIndex);
}

/** Merge editor save payload onto a session unit without wiping fields with `undefined`. */
export function applyLiquidCoolingCduSessionPatch(
  unit: LiquidCoolingCduConfiguration,
  patch: UpdateLiquidCoolingCduConfigurationInput,
): LiquidCoolingCduConfiguration {
  return core.applySessionPatch(unit, patch);
}

export function liquidCoolingCduIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  return core.idsEqual(left, right);
}

export function getLiquidCoolingCduDescriptionDisplay(
  unit: LiquidCoolingCduConfiguration,
): string {
  return unit.lcDescription?.trim() || '—';
}

/** NAME column primary — user-defined name, else "CDU N" by display order. */
export function getLiquidCoolingCduNamePrimaryDisplay(
  unit: LiquidCoolingCduConfiguration,
): string {
  return core.getNamePrimaryDisplay(unit);
}

export function formatLiquidCoolingCduTableCell(
  unit: LiquidCoolingCduConfiguration,
  column: LiquidCoolingCduTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getLiquidCoolingCduNamePrimaryDisplay(unit);
    case 'TYPE':
      return unit.lcCduType?.trim() || '—';
    case 'LEAK':
      return unit.lcLeakDetectionApproach?.trim() || '—';
    case 'REDUNDANCY':
      return unit.lcCduRedundancy?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row; text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

export function liquidCoolingCduTableColumnsForLayout(
  layout: LiquidCoolingCduTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly LiquidCoolingCduTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? LIQUID_COOLING_CDU_TABLE_NATIVE_COMPACT_COLUMNS
      : LIQUID_COOLING_CDU_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function liquidCoolingCduTableHeaderLabel(
  column: LiquidCoolingCduTableColumnKey,
  layout: LiquidCoolingCduTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'TYPE') {
      return formatRepeatableLocationTagColumnHeader(column, 'TYPE');
    }
    // Compact-only trailing metric — short header fits the fixed narrow trailing
    // column (#999). Applies to the shared compact layout (native + mobile web).
    if (column === 'REDUNDANCY') {
      return formatRepeatableLocationTagColumnHeader(column, 'RED');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

/** DESCRIPTION subline — leak detection control approach when set. */
export function getLiquidCoolingCduLeakDetectionSubline(
  unit: LiquidCoolingCduConfiguration,
): string | undefined {
  const approach = unit.lcLeakDetectionApproach?.trim();
  return approach || undefined;
}

/** NAME subline (compact) — CDU redundancy. */
export function getLiquidCoolingCduRedundancySubline(
  unit: LiquidCoolingCduConfiguration,
): string | undefined {
  const redundancy = unit.lcCduRedundancy?.trim();
  return redundancy || undefined;
}

export function getLiquidCoolingCduColumnSubline(
  column: LiquidCoolingCduTableColumnKey,
  unit: LiquidCoolingCduConfiguration,
  layout: LiquidCoolingCduTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME, TYPE carries a
  // `Leak:` label, and redundancy becomes its own trailing column (no subline —
  // the former NAME fold is now a full column).
  if (isNativeCompactLayout(layout)) {
    if (column === 'TYPE') {
      const leak = unit.lcLeakDetectionApproach?.trim();
      return leak ? `Leak: ${leak}` : undefined;
    }
    return undefined;
  }
  if (column === 'NAME') {
    return buildRepeatableNameSubline(layout, {
      compactParts:
        layout === 'compact'
          ? [getLiquidCoolingCduRedundancySubline(unit)]
          : undefined,
      locationTagName: options?.locationTagName,
    });
  }
  return undefined;
}

export function liquidCoolingCduColumnUsesDoubleLine(
  column: LiquidCoolingCduTableColumnKey,
  layout: LiquidCoolingCduTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (pill renders beneath it in the row); only the middle
    // TYPE cell double-lines (Leak:). REDUNDANCY is a single-line metric column.
    return column === 'TYPE';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return false;
}

/**
 * Primary list title — user-defined name preferred, then description, then
 * "CDU N" when displayOrder is available. Returns null when no suitable name.
 */
export function getLiquidCoolingCduDisplayName(
  unit: LiquidCoolingCduConfiguration,
): string | null {
  return core.getDisplayName(unit);
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicateLiquidCoolingCduName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  return core.buildDuplicateName(sourceName, takenNames, tempId);
}

/** Assign a serial name when the user did not provide one. */
export function ensureLiquidCoolingCduNameForCreate(
  data: UpdateLiquidCoolingCduConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdateLiquidCoolingCduConfigurationInput {
  return core.ensureNameForCreate(data, tempId, takenNames);
}

/** Client-side uniqueness check before persisting CDU session changes. */
export function validateSessionLiquidCoolingCduNames(
  session: LiquidCoolingCduConfiguration[],
): string | null {
  return core.validateSessionNames(session);
}

/** Clone fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function cloneLiquidCoolingCduForCreate(
  source: LiquidCoolingCduConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<LiquidCoolingCduConfiguration> {
  return core.cloneForCreate(source, takenNames, tempId);
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export const parseOptionalNumber = parseOptionalNumberCore;

export function createDraftLiquidCoolingCduConfiguration(
  data: UpdateLiquidCoolingCduConfigurationInput,
  displayOrder: number,
  tempId: number,
): LiquidCoolingCduConfiguration {
  return core.createDraftConfiguration(data, displayOrder, tempId);
}

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const LEGACY_LIQUID_COOLING_CDU_SYNTHETIC_ID = core.SYNTHETIC_ID;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeleteLiquidCoolingCduFromList(
  unit: LiquidCoolingCduConfiguration,
  isDraftMode: boolean,
): boolean {
  return core.canDeleteFromList(unit, isDraftMode);
}

export function hasLegacyLiquidCoolingCduRequestFields(
  source: Record<string, unknown>,
): boolean {
  return core.hasLegacyRequestFields(source);
}

/** Build one display row from pre–multi-instance flat request fields when child rows are absent. */
export function buildLegacyLiquidCoolingCduFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): LiquidCoolingCduConfiguration | null {
  return core.buildLegacyFromRequestFields(source, requestId);
}
